import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Grid, InputBase, IconButton, Select, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddPlusIcon from '../../../../assets/icons/AddPlus.svg';
import PatientActionsIcon from '../../../../assets/icons/Archive.svg';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import AddPatientDialog from './add-patient-modal';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { PATIENT_LABEL } from '../../../helper/patientLabels';

/** Builds visible page numbers with ellipsis for pagination */
const getVisiblePages = (currentPage, pageCount) => {
    if (pageCount <= 5) return Array.from({ length: pageCount }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 'ellipsis', pageCount];
    if (currentPage >= pageCount - 2) return [1, 'ellipsis', pageCount - 2, pageCount - 1, pageCount];
    return [1, 'ellipsis', currentPage, 'ellipsis', pageCount];
};

/** Formats Unix timestamp or ISO date string to readable date */
const formatDate = (val) => {
    if (!val) return null;
    const date = typeof val === 'number' ? new Date(val) : new Date(val);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const normalizePatient = (patient) => ({
    ...patient,
    patientStatus: patient.patientStatus === 'archive' ? 'archived' : patient.patientStatus || 'active',
});

/** Individual patient card in the Patient library grid */
const PatientCard = ({ patient, totalHandoutCount, onEdit, onArchive, onView, selectedRef, isSelected }) => {
    const hasHandouts = totalHandoutCount > 0;
    const dateStr = formatDate(patient.lastUpdated);
    const patientFullName = [patient.patientName, patient.patientSurname].filter(Boolean).join(' ') || 'N/A';
    const patientInfo = [patient.patientEmail, patient.clinicPatientId].filter(Boolean).join(' • ');

    return (
        <Box
            ref={isSelected ? selectedRef : null}
            sx={{
                background: isSelected ? '#E8EAF6' : '#F0F0FC',
                borderRadius: { xs: '10px', md: '20px' },
                pt: { xs: '2px', md: '14px' },
                pb: { xs: '8px', md: '14px' },
                px: { xs: '10px', md: '16px' },
                border: '1px solid #ECECFA',
                minHeight: { xs: '91px', md: '160px' },
                display: 'flex',
                flexDirection: 'row',
                gap: { xs: '8px', md: '12px' },
            }}
        >
            {/* Left column: text content — patient, details, spacer, handout count, date */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {/* Patient name */}
                <Box sx={{ mb: { xs: '2.34px', md: '10px' } }}>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '12px', md: '24px' },
                            fontWeight: 600,
                            color: '#000453',
                            letterSpacing: { xs: '-0.23px', md: '-0.48px' },
                            lineHeight: { xs: '12px', md: '24px' },
                        }}
                    >
                        {patientFullName}
                    </Typography>{' '}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '8px', md: '14px' },
                            fontWeight: 500,
                            color: 'rgba(97,96,99,0.5)',
                            lineHeight: { xs: '8px', md: '14px' },
                        }}
                    >
                        {PATIENT_LABEL}
                    </Typography>
                </Box>

                {/* Patient details — minHeight reserves 2 lines for overflow on mobile */}
                <Box sx={{ minHeight: { xs: '32px', md: 'auto' } }}>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '12px', md: '20px' },
                            fontWeight: 600,
                            color: '#000453',
                            letterSpacing: { xs: '-0.19px', md: '-0.4px' },
                            lineHeight: { xs: '16px', md: '20px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                        }}
                    >
                        {patientInfo || 'No patient details'}
                    </Typography>
                </Box>

                {/* Spacer — pushes handout count/date to card bottom; minHeight reserves empty line for overflow on mobile */}
                <Box sx={{ flex: 1, minHeight: { xs: '16px', md: 0 } }} />

                {/* Handout count */}
                <Typography
                    sx={{
                        fontSize: { xs: '9.615px', md: '20px' },
                        fontWeight: 400,
                        color: '#FF669B',
                        letterSpacing: { xs: '-0.192px', md: '-0.4px' },
                        lineHeight: { xs: '9.615px', md: '20px' },
                    }}
                >
                    {totalHandoutCount} handout{totalHandoutCount !== 1 ? 's' : ''} assigned
                </Typography>

                {/* Date — gap from handout count matches Figma (7.385px mobile, 30px desktop) */}
                {dateStr && (
                    <Typography
                        component="span"
                        sx={{
                            display: 'block',
                            mt: { xs: '7.385px', md: '30px' },
                            fontSize: { xs: '8px', md: '14px' },
                            fontWeight: 500,
                            color: '#616063',
                            opacity: 0.5,
                            letterSpacing: { xs: '-0.16px', md: '-0.28px' },
                            lineHeight: { xs: '8px', md: '14px' },
                        }}
                    >
                        Updated {dateStr}
                    </Typography>
                )}
            </Box>

            {/* Right column: action icons (top) + View handouts button (bottom) */}
            {/* Both columns stretch to card height so their bottom edges always align */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                {/* Combined action icon: Archive.svg — edit (left circle) + archive (right circle) */}
                {/* When patient is already archived, clip wrapper to show only the edit circle */}
                {/* mt aligns visual top of circles with patient name text top (font ascender offset) */}
                <Box
                    sx={{
                        position: 'relative',
                        width: patient.patientStatus === 'archived' ? { xs: 27, md: 50 } : { xs: 59, md: 110 },
                        height: { xs: 27, md: 50 },
                        flexShrink: 0,
                        mt: { xs: '6px', md: '5px' },
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="img"
                        src={PatientActionsIcon}
                        alt="Patient actions"
                        sx={{ width: { xs: 59, md: 110 }, height: '100%', display: 'block' }}
                    />
                    <Box
                        onClick={() => onEdit(patient)}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: { xs: 27, md: 50 },
                            height: '100%',
                            cursor: 'pointer',
                            borderRadius: '50%',
                            '&:hover': { background: 'rgba(255,255,255,0.15)' },
                        }}
                    />
                    {patient.patientStatus !== 'archived' && (
                        <Box
                            onClick={() => onArchive(patient.patientId)}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                width: { xs: 27, md: 50 },
                                height: '100%',
                                cursor: 'pointer',
                                borderRadius: '50%',
                                '&:hover': { background: 'rgba(255,255,255,0.15)' },
                            }}
                        />
                    )}
                </Box>

                {/* Spacer — pushes button to card bottom, mirroring left column */}
                <Box sx={{ flex: 1 }} />

                {/* View handouts button */}
                <Box
                    onClick={hasHandouts ? onView : undefined}
                    sx={{
                        borderRadius: '200px',
                        background: hasHandouts ? '#FF669B' : '#9FA1A5',
                        opacity: hasHandouts ? 1 : 0.3,
                        px: { xs: '12.5px', md: '26px' },
                        py: { xs: '7.211px', md: '15px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: hasHandouts ? 'pointer' : 'default',
                        '&:hover': hasHandouts ? { background: '#FF85B0' } : {},
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: '10px', md: '18px' },
                            fontWeight: 500,
                            color: '#FFFFFF',
                            letterSpacing: { xs: '-0.2px', md: '-0.36px' },
                            lineHeight: { xs: '10px', md: '18px' },
                            textShadow: '0px 0px 10px rgba(35,7,88,0.1)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        View handouts
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

/** My Clinic page — Patient Records (CETRA-727) */
const MyClinic = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // reserved for mobile-specific layout
    const { authStore, customisedHandoutStore, savedHandoutStore } = useContext(StoreContext);
    const [patients, setPatients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'archived'
    const [sortDirection, setSortDirection] = useState('asc'); // patient name A→Z or Z→A
    const [itemsPerPage, setItemsPerPage] = useState(6);
    const [currentPage, setCurrentPage] = useState(1);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);

    const selectedRef = useRef();
    const [selectedFromStorage, setSelectedFromStorage] = useState(null);
    // Stores return state until patients are loaded
    const returnStateRef = useRef(null);

    const showSnackbar = useSnackbar();
    const [loading, setLoading] = useState(true);

    const fetchPatients = useCallback(async () => {
        try {
            const token = authStore.idToken;
            const response = await UserApi.getPatientsByFilter(token);
            const data = (response.patients || []).map(normalizePatient);
            setPatients(data);
        } catch (err) {
            console.error('Failed to load patients', err);
            showSnackbar('Failed to load patients.', 'error');
            setPatients([]);
        } finally {
            setLoading(false);
        }
    }, [authStore.idToken, showSnackbar]);

    // Restore returnToMyClinic state and trigger fetch
    useEffect(() => {
        const returnToClinicState = sessionStorage.getItem('returnToMyClinic');
        if (returnToClinicState) {
            returnStateRef.current = JSON.parse(returnToClinicState);
            sessionStorage.removeItem('returnToMyClinic');
        }
        fetchPatients();
    }, [fetchPatients]);

    // Clear selected highlight after 4 seconds
    useEffect(() => {
        if (selectedFromStorage) {
            const timeout = setTimeout(() => setSelectedFromStorage(null), 4000);
            return () => clearTimeout(timeout);
        }
    }, [selectedFromStorage]);

    // Apply returnToMyClinic state once patients are loaded
    useEffect(() => {
        if (patients.length && returnStateRef.current) {
            const { selectedPatientId, searchPhrase } = returnStateRef.current;
            setSearchTerm(searchPhrase || '');
            setSelectedFromStorage(selectedPatientId || null);
            setTimeout(() => {
                selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
            returnStateRef.current = null;
        }
    }, [patients]);

    const handleArchive = async (patientId) => {
        try {
            await UserApi.createOrUpdatePatient(authStore.idToken, {
                patientId,
                patientStatus: 'archived',
            });
            showSnackbar('Patient archived');
            fetchPatients();
        } catch (err) {
            showSnackbar('Failed to archive patient', 'error');
        }
    };

    const handleEditPatient = (patient) => {
        setEditingPatient(patient);
        setEditDialogOpen(true);
    };

    const filteredAndSortedPatients = useMemo(() => {
        let result = patients.filter((p) => p.patientStatus === statusFilter);

        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            result = result.filter((p) =>
                ['patientName', 'patientSurname', 'patientEmail', 'clinicPatientId', 'patientStatus'].some((field) =>
                    String(p[field] || '')
                        .toLowerCase()
                        .includes(lower),
                ),
            );
        }

        return [...result].sort((a, b) => {
            const nameA = [a.patientName, a.patientSurname].filter(Boolean).join(' ').toLowerCase();
            const nameB = [b.patientName, b.patientSurname].filter(Boolean).join(' ').toLowerCase();
            return sortDirection === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
    }, [patients, statusFilter, searchTerm, sortDirection]);

    // Reset to page 1 when filters or sort change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, sortDirection, itemsPerPage]);

    const pageCount = Math.max(1, Math.ceil(filteredAndSortedPatients.length / itemsPerPage));
    const safePage = Math.min(currentPage, pageCount);
    const visiblePages = getVisiblePages(safePage, pageCount);
    const paginatedPatients = filteredAndSortedPatients.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

    return (
        <>
            {loading && <BlockingSpinner open={true} />}

            <Box
                sx={{
                    px: { xs: '16px', md: '20px' },
                    pb: { xs: '24px', md: '24px' },
                    background: '#F0F0FC',
                    minHeight: 'calc(100vh - 140px)',
                }}
            >
                <Box sx={{ maxWidth: '1360px', margin: '0 auto', pt: { xs: '20px', md: '34px' } }}>
                    {/* Page header: "Patient Records" title */}
                    <Typography
                        sx={{
                            color: '#000453',
                            fontSize: { xs: '24px', md: '40px' },
                            lineHeight: { xs: '24px', md: '40px' },
                            fontWeight: 600,
                            letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                            mb: '24px',
                        }}
                    >
                        Patient records
                    </Typography>

                    {/* Controls row: Active/Archived toggle + New Patient button + Search input */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: { xs: 'space-between', md: 'flex-start' },
                            gap: { xs: '20px', md: '16px' },
                            mb: '24px',
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* Active / Archived pill toggle */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: '#FFFFFF',
                                borderRadius: '58px',
                                p: '4px',
                                gap: '4px',
                                flexShrink: 0,
                                height: { xs: '46px', md: 'auto' },
                            }}
                        >
                            {['active', 'archived'].map((status) => (
                                <Box
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    sx={{
                                        borderRadius: '48px',
                                        px: { xs: '14px', md: '20px' },
                                        py: { xs: '9px', md: '10px' },
                                        background: statusFilter === status ? '#000453' : 'transparent',
                                        color: statusFilter === status ? '#FFFFFF' : '#000453',
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        fontSize: { xs: '16px', md: '17px' },
                                        lineHeight: { xs: '16px', md: '17px' },
                                        letterSpacing: { xs: '-0.32px', md: '-0.35px' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: 'background 0.2s, color 0.2s',
                                    }}
                                >
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </Box>
                            ))}
                        </Box>

                        {/* New Patient button */}
                        <Box
                            onClick={() => setAddDialogOpen(true)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: '9px', md: '10px' },
                                background: '#FF669B',
                                borderRadius: '200px',
                                px: { xs: '18px', md: '20px' },
                                py: { xs: '14px', md: '15px' },
                                cursor: 'pointer',
                                userSelect: 'none',
                                flexShrink: 0,
                                '&:hover': { opacity: 0.9 },
                            }}
                        >
                            <Box
                                component="img"
                                src={AddPlusIcon}
                                alt=""
                                sx={{ width: { xs: 18, md: 22 }, height: { xs: 18, md: 22 }, flexShrink: 0 }}
                            />
                            <Typography
                                sx={{
                                    fontSize: { xs: '16px', md: '18px' },
                                    fontWeight: 500,
                                    color: '#FFFFFF',
                                    letterSpacing: { xs: '-0.32px', md: '-0.36px' },
                                    lineHeight: { xs: '16px', md: '18px' },
                                    textShadow: '0px 0px 10px rgba(35,7,88,0.1)',
                                }}
                            >
                                New patient
                            </Typography>
                        </Box>

                        {/* Search patients — full-width on mobile (wraps to own row), auto-ml on desktop */}
                        <Box
                            sx={{
                                borderRadius: '89.324px',
                                background: 'linear-gradient(176.249deg, #FF669B 0%, #BD8DE8 110.58%)',
                                height: { xs: '56px', md: '50px' },
                                width: { xs: '100%', md: '251px' },
                                ml: { xs: 0, md: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                px: '16px',
                                boxSizing: 'border-box',
                                flexShrink: 0,
                            }}
                        >
                            <SearchIcon sx={{ color: '#FFFFFF', opacity: 0.8, fontSize: 20, mr: 1, flexShrink: 0 }} />
                            <InputBase
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search patients..."
                                fullWidth
                                inputProps={{ spellCheck: false }}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: 'transparent',
                                    color: 'white',
                                    '& .MuiInputBase-input': {
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        appearance: 'none',
                                        color: 'white',
                                        fontSize: '16px',
                                        lineHeight: '16px',
                                        letterSpacing: '-0.32px',
                                        fontWeight: 400,
                                        padding: 0,
                                        border: 0,
                                        borderRadius: 0,
                                        outline: 0,
                                        margin: 0,
                                        backgroundColor: 'transparent !important',
                                        boxShadow: 'none',
                                        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                        WebkitTextFillColor: 'white',
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(255,255,255,0.95)',
                                        opacity: 1,
                                    },
                                    '& input:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                        WebkitTextFillColor: 'white !important',
                                        transition: 'background-color 9999s ease-out 0s',
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Patient library white panel */}
                    <Box
                        sx={{
                            width: '100%',
                            background: '#FFFFFF',
                            borderRadius: '30px',
                            border: '1px solid #ECECFA',
                            p: { xs: '14px', md: '20px' },
                        }}
                    >
                        {/* Panel header: title + sort control */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: { xs: 'flex-start', md: 'space-between' },
                                alignItems: { xs: 'flex-start', md: 'center' },
                                mb: '18px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '20px', md: '30px' },
                                    lineHeight: { xs: '20px', md: '30px' },
                                    fontWeight: 600,
                                    color: '#000453',
                                    letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                                }}
                            >
                                Browse patients
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: { xs: '17px', md: 0 },
                                    cursor: 'pointer',
                                }}
                                onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '12px', md: '14px' },
                                        lineHeight: { xs: '12px', md: '14px' },
                                        letterSpacing: { xs: '1.826px', md: '2px' },
                                        textTransform: 'uppercase',
                                        color: 'rgba(25,25,25,0.3)',
                                    }}
                                >
                                    SORT BY{' '}
                                    <Box component="span" sx={{ color: '#000453' }}>
                                        {sortDirection === 'asc' ? 'A-Z' : 'Z-A'}
                                    </Box>
                                </Typography>
                                <IconButton sx={{ ml: 0.5, p: 0 }}>
                                    {sortDirection === 'asc' ? (
                                        <ExpandMoreIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    ) : (
                                        <ExpandLessIcon
                                            sx={{ color: '#000453', fontSize: { xs: '16px', md: '20px' } }}
                                        />
                                    )}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Patient cards grid */}
                        {!loading && filteredAndSortedPatients.length === 0 ? (
                            <Box sx={{ mt: 6, mb: 4, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ color: '#000453' }}>
                                    No {statusFilter} patients found.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2}>
                                    {paginatedPatients.map((p) => {
                                        const isSelected = p.patientId === selectedFromStorage;

                                        // Count only customised handouts for this patient (whiteboards are embedded
                                        // within customised handouts, not separately accessible from My Clinic)
                                        const customisedCount = customisedHandoutStore?.isInitialized
                                            ? Object.values(customisedHandoutStore.customisedHandoutData).filter(
                                                  (ch) =>
                                                      ch.patientId === p.patientId && ch.customisedStatus === 'active',
                                              ).length
                                            : 0;
                                        const savedCount = savedHandoutStore?.isInitialized
                                            ? Object.values(savedHandoutStore.savedHandoutData).filter(
                                                  (item) =>
                                                      item.patientId === p.patientId && item.savedStatus === 'active',
                                              ).length
                                            : 0;
                                        const totalHandoutCount = customisedCount + savedCount;

                                        return (
                                            <Grid item xs={12} sm={6} key={p.patientId}>
                                                <PatientCard
                                                    patient={p}
                                                    totalHandoutCount={totalHandoutCount}
                                                    isSelected={isSelected}
                                                    selectedRef={selectedRef}
                                                    onEdit={handleEditPatient}
                                                    onArchive={handleArchive}
                                                    onView={() => {
                                                        sessionStorage.setItem(
                                                            'returnToMyClinic',
                                                            JSON.stringify({
                                                                selectedPatientId: p.patientId,
                                                                searchPhrase: searchTerm,
                                                            }),
                                                        );
                                                        sessionStorage.setItem(
                                                            'myClinicSelectedPatient',
                                                            JSON.stringify(p),
                                                        );
                                                        navigate(`/my-clinic-patient-library/${p.patientId}`);
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* Pagination */}
                                <Box
                                    sx={{
                                        mt: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <IconButton
                                            size="small"
                                            disabled={safePage <= 1}
                                            onClick={() => setCurrentPage((v) => Math.max(1, v - 1))}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '32px',
                                                border: '1.313px solid #E5E5E5',
                                                background: '#FFFFFF',
                                                color: '#6D6D6D',
                                                '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                            }}
                                        >
                                            <ChevronLeftIcon sx={{ fontSize: '15px' }} />
                                        </IconButton>

                                        {visiblePages.map((pageItem, index) => {
                                            if (pageItem === 'ellipsis') {
                                                return (
                                                    <Box
                                                        key={`ellipsis-${index}`}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '8px',
                                                            background: '#FFFFFF',
                                                            color: '#616063',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '16px',
                                                        }}
                                                    >
                                                        ...
                                                    </Box>
                                                );
                                            }

                                            const isActive = pageItem === safePage;
                                            return (
                                                <Box
                                                    key={pageItem}
                                                    onClick={() => setCurrentPage(pageItem)}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '32px',
                                                        border: isActive ? 'none' : '1.313px solid #E5E5E5',
                                                        background: isActive ? '#FF669B' : '#FFFFFF',
                                                        color: isActive ? '#FFFFFF' : '#616063',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '13px',
                                                        fontWeight: isActive ? 400 : 500,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {pageItem}
                                                </Box>
                                            );
                                        })}

                                        <IconButton
                                            size="small"
                                            disabled={safePage >= pageCount}
                                            onClick={() => setCurrentPage((v) => Math.min(pageCount, v + 1))}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '32px',
                                                border: '1.313px solid #E5E5E5',
                                                background: '#FFFFFF',
                                                color: '#6D6D6D',
                                                '&.Mui-disabled': { opacity: 0.45, color: '#BFBFD2' },
                                            }}
                                        >
                                            <ChevronRightIcon sx={{ fontSize: '15px' }} />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Typography
                                            sx={{
                                                color: '#616063',
                                                fontSize: { xs: '14px', md: '13px' },
                                                lineHeight: { xs: '14px', md: '13px' },
                                            }}
                                        >
                                            Items per page
                                        </Typography>
                                        <Select
                                            size="small"
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            sx={{
                                                width: { xs: 63, md: 'auto' },
                                                height: 30,
                                                borderRadius: '10px',
                                                fontSize: '13px',
                                                color: '#616063',
                                                '& .MuiSelect-select': {
                                                    py: 0,
                                                    minHeight: 'unset',
                                                    lineHeight: '13px',
                                                },
                                            }}
                                        >
                                            {[6, 12, 18].map((v) => (
                                                <MenuItem key={v} value={v}>
                                                    {v}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Add patient dialog */}
            <AddPatientDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                showSnackbar={showSnackbar}
                onSubmit={async (newPatientData) => {
                    try {
                        await UserApi.createOrUpdatePatient(authStore.idToken, newPatientData);
                        showSnackbar('New patient added!', 'success');
                        fetchPatients();
                    } catch (error) {
                        console.error('Failed to add patient:', error);
                        showSnackbar('Failed to add patient.', 'error');
                    }
                }}
            />

            {/* Edit patient dialog */}
            {editingPatient && (
                <AddPatientDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    showSnackbar={showSnackbar}
                    onSubmit={async (updatedData) => {
                        try {
                            await UserApi.createOrUpdatePatient(authStore.idToken, {
                                ...updatedData,
                                patientId: editingPatient.patientId,
                            });
                            showSnackbar('Patient updated!', 'success');
                            fetchPatients();
                        } catch (error) {
                            console.error('Failed to update patient:', error);
                            showSnackbar('Failed to update patient.', 'error');
                        } finally {
                            setEditDialogOpen(false);
                        }
                    }}
                    initialValues={editingPatient}
                />
            )}
        </>
    );
};

export default observer(MyClinic);
